import {
    CognitoIdentityClient, Credentials,
    GetCredentialsForIdentityCommand,
} from '@aws-sdk/client-cognito-identity';
import { Method } from 'axios';
import { sign } from 'aws4';

export type CognitoIdentityInfo = {
    identityId: string;
    token: string;
}

interface SignedRequest {
    url: string;
    method: Method;
    host: string;
    headers: Record<string, string>;
    path: string;
    // aws4 looks for body; axios for data
    data?: object,
    body?: string,
}

export class CognitoCredentialsManager {

    private readonly cognitoInfo: CognitoIdentityInfo;
    private cachedCredentials?: Credentials;
    private readonly cognitoIdentityClient: CognitoIdentityClient;

    constructor (cognitoInfo: CognitoIdentityInfo) {
        this.cognitoInfo = cognitoInfo;
        this.cognitoIdentityClient = new CognitoIdentityClient({
            region: 'us-east-1', // required
        });
    }

    removeCredentials = () => {
        this.cachedCredentials = undefined;
    };

    private credentialsAreValid = (credentials: Credentials): boolean => {
        if (!credentials.Expiration) {
            return false;
        }

        const expirationMillis = new Date(credentials.Expiration).valueOf();
        const nowMillis = new Date().valueOf();
        const nowPlus5Mins = nowMillis + 5 * 60000;

        // check if unexpired in 5 minutes
        return expirationMillis > nowPlus5Mins;
    }

    private retrieveCredentials = async (): Promise<Credentials> => {
        const {
            identityId,
            token
        } = this.cognitoInfo;

        if (!this.cachedCredentials || !this.credentialsAreValid(this.cachedCredentials)) {
            const getCredentialsForIdentityCommand = new GetCredentialsForIdentityCommand({
                IdentityId: identityId,
                Logins: { 'cognito-identity.amazonaws.com': token },
            });
            const getCredentialsForIdentity = await this.cognitoIdentityClient.send(getCredentialsForIdentityCommand);
            if (!getCredentialsForIdentity.Credentials) {
                throw new Error('Error getting new Credentials');
            }
            this.cachedCredentials = getCredentialsForIdentity.Credentials;
        }

        return this.cachedCredentials;
    };

    getSignedRequest = async (endpoint: string, method: Method, data?: object) => {
        const url = new URL(endpoint);

        const request = {
            host: url.host,
            service: 'execute-api',
            method,
            url: url.href,
            path: url.pathname,
            headers: data ? { 'content-type': 'application/json' } : {},
            data: data || undefined,
            body: data ? JSON.stringify(data) : undefined,
        };

        const credentials = await this.retrieveCredentials();
        let signedRequest = sign(request, {
            secretAccessKey: credentials.SecretKey,
            accessKeyId: credentials.AccessKeyId,
            sessionToken: credentials.SessionToken,
        }) as SignedRequest;

        delete signedRequest.headers['Host'];
        delete signedRequest.headers['Content-Length'];
        return signedRequest;
    };
}
